import React from 'react';
import axios from 'axios';
import { Formik, useFormikContext } from 'formik';

import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Caption from '~/components/core/TextComponents/Caption';
import {
  EMAIL_CONFIGURATION_TOKENS,
  mapTokensToSuggestionOptions,
  noSubOrgEmailSignatureTokens,
  useTokensFetcher,
} from '~/components/SystemConfiguration/EmailConfiguration/emailConfigurationTokens';
import EmailSignatureEditorField from '~/components/SystemConfiguration/EmailConfiguration/EmailSignatureEditorField';
import { useAvailableEmailSenders } from '~/components/SystemConfiguration/Users/hooks/useAvailableEmailSenders';
import useDataFetcher from '~/components/useDataFetcher';
import type { EmailSignatureResponseData } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import ButtonsContainer from './core/Atomic/Buttons/ButtonsContainer';
import useCanSendAsOtherAdjuster from './hooks/useCanSendAsOtherAdjuster';
import { useCms } from './hooks/useCms';
import type {
  PersonalEmailSignatureDialogInnerProps,
  PersonalEmailSignatureDialogProps,
  PersonalEmailSignatureFormProps,
  PersonalEmailSignatureFormValues,
} from './types/user-menu-types';
import CardDialog from './CardDialog';

const SELF_EMAIL_SIGNATURE_URL = '/api/v1/users/me/email_signature';

// The self-service route stays the one a user reaches for their own signature: it is unpermissioned,
// while the per-user route below sits behind the send-as authority a plain adjuster does not hold.
const getEmailSignatureUrl = (userId: string, isSelf: boolean) =>
  isSelf ? SELF_EMAIL_SIGNATURE_URL : `/api/v1/users/${userId}/email_signature`;

const SkeletonLoader = () => <SkeletonTable rowsCount={4} columnsCount={1} />;

function PersonalEmailSignatureDialog({ onClose }: PersonalEmailSignatureDialogProps) {
  const { user, userOrganization } = useCms();

  // Same authority the send-as picker uses, which is the authority the per-user signature route
  // enforces. It only decides whether to offer the picker; the route scopes the users itself.
  const canSendAsOtherAdjuster = useCanSendAsOtherAdjuster();
  const [selectedUserId, setSelectedUserId] = React.useState<string>(user.id);

  // A personal signature is not sub-org scoped, so SUB_ORGANIZATION_NAME is not offered.
  const signatureTokens = userOrganization.is_claims_inbox_enabled
    ? [...noSubOrgEmailSignatureTokens, ...EMAIL_CONFIGURATION_TOKENS.claimEmailAddressToken]
    : noSubOrgEmailSignatureTokens;

  const {
    data: tokensData,
    isLoading: isLoadingTokens,
    isError: isErrorTokens,
  } = useTokensFetcher(userOrganization.id, signatureTokens);

  // The same list the send-as picker offers, and the same scope the per-user signature route
  // authorizes: the server restricts it to the acting user's managed users unless the organization
  // turned `restrict_send_as_to_managed_users` off.
  const { users: availableSenders, isLoading: isLoadingSenders } = useAvailableEmailSenders({
    organizationId: userOrganization.id,
    enabled: canSendAsOtherAdjuster,
  });

  // The server includes the acting user in that list, so they are filtered out here and re-added as
  // the first option -- their own signature is what this dialog opens on.
  const teamMembers = availableSenders.filter((member) => member.id !== user.id);
  const isSelfSelected = selectedUserId === user.id;
  const selectedTeamMemberName = teamMembers.find((member) => member.id === selectedUserId)?.username;

  return (
    <CardDialog title="Personal Email Signatures" isDialog onClose={onClose} maxWidth="md" fullWidth>
      {/* A failed senders fetch is deliberately not an error state for the whole dialog: it costs
          the picker, not the acting user's own signature, which is what every user came here for.
          The fetch reports its own failure. */}
      <LoadingSwitch
        isLoading={isLoadingTokens || isLoadingSenders}
        isError={isErrorTokens}
        LoadingComponent={SkeletonLoader}
      >
        {teamMembers.length ? (
          <SingleSelectField
            id="email_signature_user_id"
            label="User"
            value={selectedUserId}
            onChange={(userId: string) => setSelectedUserId(userId)}
            className="my-16 w-1/2 min-w-[150px] max-w-[280px]"
          >
            <MenuItem key={user.id} value={user.id}>
              {`${user.username} (me)`}
            </MenuItem>
            {teamMembers.map((member) => (
              <MenuItem key={member.id} value={member.id}>
                {member.username}
              </MenuItem>
            ))}
          </SingleSelectField>
        ) : null}
        {/* Keyed on the selected user so a switch mounts a fresh form: the fetch then starts in its
            loading state, and the editor -- which captures its content at construction and ignores
            later prop changes -- is re-mounted with the newly selected user's signature. */}
        <PersonalEmailSignatureForm
          key={selectedUserId}
          url={getEmailSignatureUrl(selectedUserId, isSelfSelected)}
          targetUsername={isSelfSelected ? undefined : selectedTeamMemberName}
          organizationId={userOrganization.id}
          suggestionOptions={mapTokensToSuggestionOptions(tokensData)}
          onClose={onClose}
        />
      </LoadingSwitch>
    </CardDialog>
  );
}

const PersonalEmailSignatureForm: React.FC<PersonalEmailSignatureFormProps> = ({
  url,
  targetUsername,
  organizationId,
  suggestionOptions,
  onClose,
}) => {
  const {
    data: signatureData,
    isLoading: isLoadingSignature,
    isError: isErrorSignature,
  } = useDataFetcher<EmailSignatureResponseData>(url);

  const handleSubmit = async (values: PersonalEmailSignatureFormValues) => {
    try {
      await axios.put(url, values);
      onClose();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  return (
    /* Formik must not mount before the stored signature has loaded: the editor captures its
       content when it mounts and ignores later changes, so initialValues has to be final. */
    <LoadingSwitch isLoading={isLoadingSignature} isError={isErrorSignature} LoadingComponent={SkeletonLoader}>
      <Formik<PersonalEmailSignatureFormValues>
        initialValues={{
          email_signature_html_template: signatureData?.email_signature_html_template || '',
          // The field is optional in the generated contract because the schema gives it a
          // default, so the nullish fallback is what the type demands rather than a guess --
          // and `true` is that same default.
          email_signature_active: signatureData?.email_signature_active ?? true,
        }}
        onSubmit={handleSubmit}
      >
        <PersonalEmailSignatureDialogInner
          onClose={onClose}
          organizationId={organizationId}
          suggestionOptions={suggestionOptions}
          targetUsername={targetUsername}
        />
      </Formik>
    </LoadingSwitch>
  );
};

const PersonalEmailSignatureDialogInner: React.FC<PersonalEmailSignatureDialogInnerProps> = ({
  onClose,
  organizationId,
  suggestionOptions,
  targetUsername,
}) => {
  const { values, setFieldValue, handleSubmit, isSubmitting } = useFormikContext<PersonalEmailSignatureFormValues>();

  return (
    <>
      <SwitchFormik
        id="email_signature_active"
        label={targetUsername ? `Use ${targetUsername}'s personal signature` : 'Use my personal signature'}
        hideErrorGap
      />
      <Caption variant={Caption.VARIANTS.LABEL}>
        {getSignatureCaption(values.email_signature_active, targetUsername)}
      </Caption>
      <EmailSignatureEditorField
        organizationId={organizationId}
        label="Signature"
        content={values.email_signature_html_template}
        onChange={(value) => setFieldValue('email_signature_html_template', value)}
        suggestionOptions={suggestionOptions}
        isLoadingTokens={false}
      />
      <ButtonsContainer>
        <CancelButton onClick={onClose} />
        <Button variant="contained" color="primary" onClick={() => handleSubmit()} disabled={isSubmitting}>
          Save
        </Button>
      </ButtonsContainer>
    </>
  );
};

// The switch is the only thing that decides between the personal and the organization signature, and
// it is non-destructive either way -- so both states describe what the stored content below is for.
const getSignatureCaption = (isActive: boolean, targetUsername?: string): string => {
  if (targetUsername) {
    return isActive
      ? `The signature below replaces the organization's signature on emails ${targetUsername} sends. Leave it empty to send no signature at all.`
      : `The organization's signature is used on emails ${targetUsername} sends. The signature below is kept, so it can be switched back on at any time.`;
  }

  return isActive
    ? "The signature below replaces your organization's signature on emails you send. Leave it empty to send no signature at all."
    : "Your organization's signature is used on emails you send. The signature below is kept, so you can switch it back on at any time.";
};

export default PersonalEmailSignatureDialog;
